import React from 'react';

export const uid = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const move = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

const baseInput =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'outline-none transition focus:border-[#009FE3] focus:ring-2 focus:ring-[#009FE3]/20 ' +
  'disabled:bg-slate-100 disabled:text-slate-400';

export const Input = (props) => <input {...props} className={`${baseInput} ${props.className || ''}`} />;

export const Textarea = (props) => (
  <textarea {...props} className={`${baseInput} min-h-[80px] resize-y ${props.className || ''}`} />
);

export const Select = ({ children, ...props }) => (
  <select {...props} className={`${baseInput} ${props.className || ''}`}>{children}</select>
);

const variants = {
  primary: 'bg-[#009FE3] text-white hover:bg-[#0089c4] disabled:bg-slate-300',
  dark: 'bg-slate-900 text-white hover:bg-slate-700 disabled:bg-slate-300',
  ghost: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
  danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50',
};

export function Button({ variant = 'ghost', className = '', ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm
        font-semibold transition disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    />
  );
}

export function Card({ children, className = '' }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>{children}</div>
  );
}

export function Badge({ tone = 'slate', children }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600',
    blue: 'bg-[#009FE3]/10 text-[#0079ac]',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tones[tone]}`}>
      {children}
    </span>
  );
}

/**
 * Sticky save bar. Shows whether the current draft differs from what's stored.
 */
export function SaveBar({ dirty, saving, onSave, onReset, error, count }) {
  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={onSave} disabled={!dirty || saving}>
          {saving ? 'Spremam…' : 'Spremi promjene'}
        </Button>
        <Button onClick={onReset} disabled={!dirty || saving}>Odbaci</Button>
        <span className="text-xs text-slate-500">
          {dirty ? 'Imaš nespremljene promjene.' : 'Sve je spremljeno.'}
          {typeof count === 'number' && ` · ${count} zapisa`}
        </span>
        {error && <span className="text-xs font-semibold text-red-600">{error}</span>}
      </div>
    </div>
  );
}

/** Collapsible row used by the list editors. */
export function Row({ open, onToggle, summary, meta, children, onDelete, onUp, onDown }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <span className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
          <span className="flex-1">
            <span className="block text-sm font-semibold text-slate-900">{summary}</span>
            {meta && <span className="mt-0.5 flex flex-wrap items-center gap-1.5">{meta}</span>}
          </span>
        </button>
        <div className="flex items-center gap-1">
          {onUp && <Button className="!px-2 !py-1" onClick={onUp} title="Gore">↑</Button>}
          {onDown && <Button className="!px-2 !py-1" onClick={onDown} title="Dolje">↓</Button>}
          {onDelete && <Button variant="danger" className="!px-2 !py-1" onClick={onDelete} title="Obriši">✕</Button>}
        </div>
      </div>
      {open && <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-4">{children}</div>}
    </Card>
  );
}
